interface AuthTokens {
  access: string
  refresh: string
}

interface User {
  email: string
  // Add other user fields as needed from your backend
}

const TOKEN_KEY = "regulagent_tokens"
const USER_KEY = "regulagent_user"

let refreshPromise: Promise<AuthTokens | null> | null = null

import { API_BASE_URL } from "./config"

/**
 * Store auth tokens in localStorage
 */
export function setTokens(tokens: AuthTokens): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
  }
}

/**
 * Get auth tokens from localStorage
 */
export function getTokens(): AuthTokens | null {
  if (typeof window === "undefined") return null

  const tokens = localStorage.getItem(TOKEN_KEY)
  return tokens ? JSON.parse(tokens) : null
}

/**
 * Remove auth tokens from localStorage
 */
export function clearTokens(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  }
}

/**
 * Store user data in localStorage
 */
export function setUser(user: User): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  }
}

/**
 * Get user data from localStorage
 */
export function getUser(): User | null {
  if (typeof window === "undefined") return null

  const user = localStorage.getItem(USER_KEY)
  return user ? JSON.parse(user) : null
}

/**
 * Login with email and password
 */
export async function login(email: string, password: string): Promise<{ tokens: AuthTokens; user: User }> {
  const tokenUrl = `${API_BASE_URL}/api/auth/token/`

  const response = await fetch(tokenUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
    credentials: "include",
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Login failed" }))
    throw new Error(error.detail || "Login failed")
  }

  const tokens: AuthTokens = await response.json()

  setTokens(tokens)
  scheduleTokenRefresh()

  const user: User = { email }
  setUser(user)

  return { tokens, user }
}

/**
 * Refresh access token using refresh token.
 * Uses a module-level lock so concurrent callers share one in-flight request.
 */
export async function refreshAccessToken(): Promise<AuthTokens | null> {
  // If a refresh is already in progress, wait for it instead of firing another
  if (refreshPromise) {
    return refreshPromise
  }

  const tokens = getTokens()
  if (!tokens?.refresh) return null

  refreshPromise = (async () => {
    try {
      const refreshUrl = `${API_BASE_URL}/api/auth/refresh/`

      const response = await fetch(refreshUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: tokens.refresh }),
      })

      if (!response.ok) {
        clearTokens()
        return null
      }

      const data = await response.json()
      // Backend rotates refresh tokens, so save the new refresh token too
      const newTokens: AuthTokens = {
        access: data.access,
        refresh: data.refresh || tokens.refresh,
      }
      setTokens(newTokens)
      return newTokens
    } catch (error) {
      console.error("[v0] Token refresh failed:", error)
      clearTokens()
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null

function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp ? payload.exp * 1000 : null // convert to ms
  } catch {
    return null
  }
}

export function scheduleTokenRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  const tokens = getTokens()
  if (!tokens?.access) return

  const expiry = getTokenExpiry(tokens.access)
  if (!expiry) return

  // Refresh 2 minutes before expiry
  const refreshAt = expiry - Date.now() - 2 * 60 * 1000
  if (refreshAt <= 0) {
    // Already expired or about to — refresh now
    refreshAccessToken().then((newTokens) => {
      if (newTokens) scheduleTokenRefresh()
    })
    return
  }

  refreshTimer = setTimeout(async () => {
    const newTokens = await refreshAccessToken()
    if (newTokens) {
      scheduleTokenRefresh() // schedule next refresh
    }
  }, refreshAt)
}

export function logout(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  clearTokens()

  if (typeof window !== "undefined") {
    // Django backend logout endpoint (optional - backend doesn't require it)
    // Just clear tokens and redirect
    window.location.href = "/signin"
  }
}

export function isAuthenticated(): boolean {
  if (typeof window !== "undefined" && import.meta.env.MODE === "development") {
    return true
  }
  const tokens = getTokens()
  return !!tokens?.access
}

// Schedule refresh on app load if tokens exist
if (typeof window !== "undefined") {
  scheduleTokenRefresh()
}
